// Types for the CUSTOMER bulk buy-electricity (bulk purchase) authoring flow.
// The read/list model lives in ./bulk-purchase.ts; this file adds the
// create/validate/upload contracts consumed by the customer flow. These mirror
// the backend Vending bulk endpoints already used by the mobile app
// (POST /api/vending/bulk/purchase/{validate/manual,validate/xlsx,upload/{card,wallet}}).

export type BulkServiceType = "Prepaid" | "Postpaid";

/**
 * A single client-authored line item. `meterNumber` holds the meter number for
 * Prepaid and the account number for Postpaid (the backend uses one field for
 * both). `id` is a client-only identifier used for list keys / edit / delete.
 */
export interface BulkLineItem {
	id: string;
	serviceType: BulkServiceType;
	meterNumber: string;
	amount: number;
	/** Display name resolved from the validation response (accountName), when available. */
	name?: string;
	/** Populated after validation. */
	isValid?: boolean;
	errors?: string[];
}

/** Item shape sent to validate/manual and the upload endpoints. */
export interface BulkPurchaseItemRequest {
	meterNumber: string;
	serviceType: BulkServiceType;
	amount: number;
}

export interface RowValidationResult {
	index: number;
	meterNumber: string;
	serviceType: string;
	amount: number;
	vendorId?: number;
	isValidated?: boolean;
	isValid: boolean;
	errors: string[];
}

export interface ValidateBulkPurchaseResponse {
	rows: RowValidationResult[];
	validCount: number;
	invalidCount: number;
	errors: string[];
}

export interface UploadBulkPurchaseWalletRequest {
	pin: string;
	items: BulkPurchaseItemRequest[];
}

export interface UploadBulkPurchaseCardRequest {
	pin: string;
	returnUrl: string;
	paymentTypes: string[];
	items: BulkPurchaseItemRequest[];
}

/** Response from POST .../upload/card — redirect the customer to `returnUrl`. */
export interface BulkPurchaseCardResult {
	reference: string;
	returnUrl: string;
}

export interface CustomerBulkPurchaseSearchParams {
	Page?: number;
	PageSize?: number;
	Reference?: string;
}

/** Maps a validated backend row back onto a client line item (preserving the client id). */
export function rowToLineItem(row: RowValidationResult, id: string): BulkLineItem {
	const serviceType: BulkServiceType = String(row.serviceType).toLowerCase().startsWith("post") ? "Postpaid" : "Prepaid";
	return {
		id,
		serviceType,
		meterNumber: row.meterNumber,
		amount: row.amount,
		isValid: row.isValid,
		errors: row.errors ?? [],
	};
}

/** Converts client line items to the request payload item shape. */
export function lineItemsToRequest(items: BulkLineItem[]): BulkPurchaseItemRequest[] {
	return items.map(i => ({
		meterNumber: i.meterNumber,
		serviceType: i.serviceType,
		amount: i.amount,
	}));
}
